import { normalizeEmail } from './emailUtils';
import { formatSupabaseError } from './formatSupabaseError';
import { supabase } from './supabase';

/** Wide-table row: one person, one role column per portal (null = no access). */
export type PortalAccessRow = {
  id: string;
  email: string;
  display_name: string | null;
  username: string | null;
  auth_id: string | null;
  department_id: string | null;
  crm_role: string | null;
  it_role: string | null;
  dem_role: string | null;
  forms_role: string | null;
  accounts_role: string | null;
  report_role: string | null;
  purchasing_role: string | null;
  design_role: string | null;
  calendar_role: string | null;
  removed_at: string | null;
  removed_by: string | null;
  removed_reason: string | null;
  created_at?: string;
  updated_at?: string;
};

export const PORTAL_ACCESS_SELECT =
  'id, email, display_name, username, auth_id, department_id, crm_role, it_role, dem_role, forms_role, accounts_role, report_role, purchasing_role, design_role, calendar_role, removed_at, removed_by, removed_reason, created_at, updated_at';

function accessDb() {
  return supabase.schema('access_management');
}

export function isPortalAccessTableMissing(error: {
  code?: string;
  message?: string;
} | null | undefined): boolean {
  if (!error) return false;
  const code = (error.code ?? '').toUpperCase();
  const message = error.message ?? '';
  return (
    code === '42P01' ||
    code === 'PGRST205' ||
    code === 'PGRST106' ||
    /portal_access|access_management|schema cache|could not find the table|relation .* does not exist/i.test(
      message,
    )
  );
}

/** Current user's membership row (login / chooser). */
export async function fetchOwnPortalAccessRow(
  email: string,
  authUserId: string,
): Promise<PortalAccessRow | null> {
  const db = accessDb();
  const { data: byAuth, error: authError } = await db
    .from('portal_access')
    .select(PORTAL_ACCESS_SELECT)
    .eq('auth_id', authUserId)
    .maybeSingle();

  if (!authError && byAuth) {
    return byAuth as PortalAccessRow;
  }
  if (authError && !isPortalAccessTableMissing(authError) && authError.code !== 'PGRST116') {
    throw new Error(formatSupabaseError(authError));
  }
  if (isPortalAccessTableMissing(authError)) return null;

  const { data, error } = await db
    .from('portal_access')
    .select(PORTAL_ACCESS_SELECT)
    .ilike('email', normalizeEmail(email))
    .maybeSingle();

  if (error) {
    if (isPortalAccessTableMissing(error)) return null;
    throw new Error(formatSupabaseError(error));
  }
  return (data as PortalAccessRow | null) ?? null;
}

/** Admin Hub: all people (includes soft-removed). */
export async function fetchAllPortalAccessRows(): Promise<PortalAccessRow[]> {
  const { data, error } = await accessDb()
    .from('portal_access')
    .select(PORTAL_ACCESS_SELECT)
    .order('email');

  if (error) {
    if (isPortalAccessTableMissing(error)) return [];
    throw new Error(formatSupabaseError(error));
  }
  return (data ?? []) as PortalAccessRow[];
}

export type PortalAccessKind =
  | 'crm'
  | 'it'
  | 'dem'
  | 'forms'
  | 'accounts'
  | 'report'
  | 'purchasing'
  | 'design'
  | 'calendar';

function isMissingRpc(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  const msg = `${error.code ?? ''} ${error.message ?? ''}`;
  return /PGRST202|42883|does not exist|Could not find the function|admin_set_portal_role|admin_set_display_name|sync_my_auth_on_login/i.test(
    msg,
  );
}

/** Phase 3: grant / update / revoke a portal role (wide SoT + legacy mirror). */
export async function adminSetPortalRole(input: {
  email: string;
  portal: PortalAccessKind;
  role: string | null;
  displayName?: string | null;
  departmentId?: string | null;
  reason?: string | null;
}): Promise<'ok' | 'rpc_missing'> {
  const { error } = await accessDb().rpc('admin_set_portal_role', {
    p_email: normalizeEmail(input.email),
    p_portal: input.portal,
    p_role: input.role,
    p_display_name: input.displayName?.trim() || null,
    p_department_id: input.departmentId ?? null,
    p_reason: input.reason?.trim() || null,
  });
  if (error) {
    if (isMissingRpc(error) || isPortalAccessTableMissing(error)) return 'rpc_missing';
    throw new Error(formatSupabaseError(error));
  }
  return 'ok';
}

export async function adminSetPortalDisplayName(
  email: string,
  displayName: string,
): Promise<'ok' | 'rpc_missing'> {
  const { error } = await accessDb().rpc('admin_set_display_name', {
    p_email: normalizeEmail(email),
    p_display_name: displayName.trim() || null,
  });
  if (error) {
    if (isMissingRpc(error) || isPortalAccessTableMissing(error)) return 'rpc_missing';
    throw new Error(formatSupabaseError(error));
  }
  return 'ok';
}

/** Bind auth_id on wide (+ mirror) at login. Returns null when no membership / RPC missing. */
export async function syncMyPortalAccessAuth(): Promise<PortalAccessRow | null> {
  const { data, error } = await accessDb().rpc('sync_my_auth_on_login');
  if (error) {
    const msg = `${error.code ?? ''} ${error.message ?? ''}`;
    if (
      isMissingRpc(error) ||
      isPortalAccessTableMissing(error) ||
      /PGRST205|schema cache|allowed_emails|dem_allowed_emails/i.test(msg)
    ) {
      return null;
    }
    throw new Error(formatSupabaseError(error));
  }
  return (data as PortalAccessRow | null) ?? null;
}

export const PORTAL_ROLE_COLUMN: Record<PortalAccessKind, keyof PortalAccessRow> = {
  crm: 'crm_role',
  it: 'it_role',
  dem: 'dem_role',
  forms: 'forms_role',
  accounts: 'accounts_role',
  report: 'report_role',
  purchasing: 'purchasing_role',
  design: 'design_role',
  calendar: 'calendar_role',
};

/** Active members for one portal (role column not null). */
export async function fetchPortalMembersByKind(
  kind: PortalAccessKind,
): Promise<
  {
    id: string;
    email: string;
    display_name: string | null;
    role: string;
    username: string | null;
    auth_id: string | null;
    department_id: string | null;
    created_at?: string;
  }[]
> {
  const col = PORTAL_ROLE_COLUMN[kind];
  const { data, error } = await accessDb()
    .from('portal_access')
    .select(PORTAL_ACCESS_SELECT)
    .is('removed_at', null)
    .not(col, 'is', null)
    .order('email');

  if (error) {
    if (isPortalAccessTableMissing(error)) return [];
    throw new Error(formatSupabaseError(error));
  }

  return ((data ?? []) as PortalAccessRow[]).map((row) => ({
    id: row.id,
    email: row.email,
    display_name: row.display_name,
    role: String(row[col] ?? 'user'),
    username: row.username,
    auth_id: row.auth_id,
    department_id: row.department_id,
    created_at: row.created_at,
  }));
}

export type PortalAccessRemoval = {
  id: string;
  portal: string;
  email: string;
  display_name: string | null;
  role: string | null;
  deleted_by: string | null;
  deleted_at: string;
  reason: string | null;
};

export async function fetchPortalAccessRemovals(
  portal?: PortalAccessKind,
  limit = 50,
): Promise<PortalAccessRemoval[]> {
  let q = accessDb()
    .from('portal_access_removals')
    .select('id, portal, email, display_name, role, deleted_by, deleted_at, reason')
    .order('deleted_at', { ascending: false })
    .limit(limit);
  if (portal) q = q.eq('portal', portal);
  const { data, error } = await q;
  if (error) {
    if (isPortalAccessTableMissing(error) || /portal_access_removals|does not exist/i.test(error.message ?? '')) {
      return [];
    }
    throw new Error(formatSupabaseError(error));
  }
  return (data ?? []) as PortalAccessRemoval[];
}

export async function emailForPortalAccessId(id: string): Promise<string | null> {
  const { data, error } = await accessDb()
    .from('portal_access')
    .select('email')
    .eq('id', id)
    .maybeSingle();
  if (error) {
    if (isPortalAccessTableMissing(error)) return null;
    throw new Error(formatSupabaseError(error));
  }
  const email = (data as { email?: string } | null)?.email;
  return email ? normalizeEmail(email) : null;
}
